
      import { createComponent } from '@lit/react'
      import { type EventName } from '@lit/react'
      import * as React from 'react'

      import Component from '../../components/radio-button/radio-button.component.js'
      import type { DdsChangeEvent } from '../../events/events'
import type { DdsBlurEvent } from '../../events/events'
import type { DdsFocusEvent } from '../../events/events'
import type { DdsInputEvent } from '../../events/events'
      export type { DdsChangeEvent } from '../../events/events'
export type { DdsBlurEvent } from '../../events/events'
export type { DdsFocusEvent } from '../../events/events'
export type { DdsInputEvent } from '../../events/events'

      const tagName = 'dap-ds-radio-button'

      /**
 * `dap-ds-radio-button`
 * @summary A radio button is a graphical control element that allows the user to choose only one of a predefined set of mutually exclusive options.
 * @element dap-ds-radio-button
 * @title - Radio button
 *
 * @event {{ checked: boolean, value: string, disabled: boolean, type: 'radio' }} dds-change - Fired when the radio button is checked.
 * @event {{ void }} dds-blur - Emitted when the radio button loses focus.
 * @event {{ void }} dds-focus - Emitted when the radio button gains focus.
 * @event {{ checked: boolean, value: string, disabled: boolean }} dds-input - Emitted when the radio button receives input.
 *
 * @property {string} name - The name of the radio button.
 * @property {string} value - The value of the radio button.
 * @property {boolean} checked - Whether the radio button is checked.
 * @property {string} label - The label of the radio button.
 * @property {string} description - The description of the radio button.
 * @property {boolean} disabled - Whether the radio button is disabled.
 * @property {boolean} required - Whether the radio button is required.
 * @property {'xs' | 'sm' | 'md' | 'lg'} size - The size of the radio button.
 * @property {'left' | 'right'} labelPlacement - The placement of the label.
 * @property {'top' | 'bottom'} descriptionPlacement - The placement of the description.
 * @property {boolean} subtle - The weight of the label.
 * @property {string} feedback - The feedback of the radio button.
 * @property {'negative' | 'positive' | 'warning'} feedbackType - The feedback type of the radio button.
 * @property {boolean} invalid - The invalid state of the radio button.
 * @property {boolean} optional - The optional state of the radio button.
 * @property {string} optionalLabel - The optional label of the radio button.
 * @property {boolean} preventDefault - Whether the radio button should prevent the default action.
 * @property {boolean} focusable - Whether the radio button is focusable.
 * @property {'normal' | 'background'} type - The type of the radio button.
 * @property {boolean} border - This sets up a border around the radio button, when true.
 *
 * @csspart base - The main radio button container.
 * @csspart wrapper - The wrapper of the radio button.
 * @csspart base-label - The main label container.
 * @csspart label - The label of the radio button.
 * @csspart input - The native input of the radio button.
 * @csspart control - The control of the radio button.
 * @csspart label-container - The label container of the radio button.
 * @csspart description - The description of the radio button.
 *
 * @cssproperty --dds-radio-size - Sets the size of the radio button. Default: var(--dds-spacing-600)
 * @cssproperty --dds-radio-border-width - Sets the border width of the radio button. Default: var(--dds-border-width-large)
 * @cssproperty --dds-radio-border-radius - Sets the border radius of the radio button. Default: var(--dds-radius-rounded)
 * @cssproperty --dds-radio-border-color - Sets the border color of the radio button. Default: var(--dds-border-neutral-base)
 * @cssproperty --dds-radio-background-color - Sets the background color of the radio button. Default: var(--dds-background-neutral-base)
 * @cssproperty --dds-radio-hover-border-color - Sets the border color of the radio button on hover. Default: var(--dds-border-neutral-medium)
 * @cssproperty --dds-radio-hover-background-color - Sets the background color of the radio button on hover. Default: var(--dds-background-neutral-medium)
 * @cssproperty --dds-radio-active-border-color - Sets the border color of the radio button when active. Default: var(--dds-border-neutral-strong)
 * @cssproperty --dds-radio-active-background-color - Sets the background color of the radio button when active. Default: var(--dds-background-neutral-strong)
 * @cssproperty --dds-radio-checked-background-color - Sets the background color of the checked radio button. Default: var(--dds-background-brand-base-inverted)
 * @cssproperty --dds-radio-checked-hover-background-color - Sets the background color of the checked radio button on hover. Default: var(--dds-background-brand-medium-inverted)
 * @cssproperty --dds-radio-checked-active-background-color - Sets the background color of the checked radio button when active. Default: var(--dds-background-brand-strong-inverted)
 * @cssproperty --dds-radio-disabled-background-color - Sets the background color of the disabled radio button. Default: var(--dds-background-neutral-disabled)
 * @cssproperty --dds-radio-icon-background-color - Sets the background color of the radio button icon. Default: var(--dds-transparent-white-strong-static)
 * @cssproperty --dds-radio-disabled-icon-background-color - Sets the background color of the disabled radio button icon. Default: var(--dds-background-neutral-stronger)
 * @cssproperty --dds-radio-invalid-border-color - Sets the border color of the invalid radio button. Default: var(--dds-border-negative-base)
 * @cssproperty --dds-radio-invalid-background-color - Sets the background color of the invalid radio button. Default: var(--dds-background-negative-base)
 * @cssproperty --dds-radio-invalid-hover-border-color - Sets the border color of the invalid radio button on hover. Default: var(--dds-border-negative-medium)
 * @cssproperty --dds-radio-invalid-hover-background-color - Sets the background color of the invalid radio button on hover. Default: var(--dds-background-negative-medium)
 * @cssproperty --dds-radio-invalid-active-border-color - Sets the border color of the invalid radio button when active. Default: var(--dds-border-negative-strong)
 * @cssproperty --dds-radio-invalid-active-background-color - Sets the background color of the invalid radio button when active. Default: var(--dds-background-negative-strong)
 * @cssproperty --dds-radio-invalid-checked-background-color - Sets the background color of the invalid checked radio button. Default: var(--dds-background-negative-base-inverted)
 * @cssproperty --dds-radio-invalid-checked-hover-background-color - Sets the background color of the invalid checked radio button on hover. Default: var(--dds-background-negative-medium-inverted)
 * @cssproperty --dds-radio-invalid-checked-active-background-color - Sets the background color of the invalid checked radio button when active. Default: var(--dds-background-negative-strong-inverted)
 * @cssproperty --dds-radio-icon-size - Sets the size of the radio button icon. Default: var(--dds-spacing-300)
 */
      const reactWrapper = createComponent({
        tagName,
        elementClass: Component,
        react: React,
        events: {
          onDdsChange: 'dds-change' as EventName<DdsChangeEvent>,
onDdsBlur: 'dds-blur' as EventName<DdsBlurEvent>,
onDdsFocus: 'dds-focus' as EventName<DdsFocusEvent>,
onDdsInput: 'dds-input' as EventName<DdsInputEvent>
        },
        displayName: "DapDSRadioButton"
      })

      export default reactWrapper
    